import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"
import {
  MCP_TOOL_GROUP_LABELS,
  mcpToolGroup,
  type McpToolGroup,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import {
  localIsoDate,
  type UsageDailyTotal,
} from "@/v2-backoffice-client/usage/usage.utils"
import type { UsageMcpCountFragment } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpCount.fragment"
import type { UsageMcpDayFragment } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpDay.fragment"

/** One UTC day × MCP server of a scope's MCP activity, as the queries return it. */
export type UsageMcpDayRow = UsageMcpDayFragment

/** One tool / client / refusal reason of the 28-day breakdown. */
export type UsageMcpCountRow = UsageMcpCountFragment

export type UsageMcpWindow = {
  /** MCP servers (app codes) with any activity in the series. */
  apps: string[]
  calls28d: number
  callsPrev28d: number
  calls7d: number
  /** 28 d vs the 28 days before; null without a baseline. */
  trendPct: number | null
  errors28d: number
  /** Share of 28-day calls that failed, 0–100 with one decimal; null without calls. */
  errorPct28d: number | null
  denied28d: number
  /** Days with at least one tool call in the last 28. */
  activeDays28d: number
  /** Last day with a tool call, YYYY-MM-DD; null when none in the series. */
  lastCallDay: string | null
  /** Last day with a refused sign-in, YYYY-MM-DD; null when none. */
  lastDeniedDay: string | null
}

function daysAgo(anchor: Date, days: number) {
  return localIsoDate(new Date(anchor.getTime() - days * ONE_DAY_IN_MS))
}

/** Percentage change, one decimal; null without a baseline. */
export function mcpTrendPct(current: number, previous: number) {
  if (previous <= 0) return null
  return Math.round(((current - previous) * 1000) / previous) / 10
}

/**
 * Rolls the daily series up into the window numbers the MCP sections
 * show. The 28-day window ends today (anchor); the previous window is the
 * 28 days before it, which is the trend base. Refused sign-ins never count
 * as calls. The per-tool / per-client split comes pre-aggregated from the
 * breakdown procs and is not derived here.
 */
export function deriveMcpWindow(
  days: UsageMcpDayRow[],
  anchor: Date = new Date(),
): UsageMcpWindow {
  const from7 = daysAgo(anchor, 7)
  const from28 = daysAgo(anchor, 28)
  const from56 = daysAgo(anchor, 56)

  const apps = new Set<string>()
  const activeDays = new Set<string>()
  let calls28d = 0
  let callsPrev28d = 0
  let calls7d = 0
  let errors28d = 0
  let denied28d = 0
  let lastCallDay: string | null = null
  let lastDeniedDay: string | null = null

  for (const row of days) {
    const day = String(row.Date).slice(0, 10)
    if (row.Calls > 0 || row.Denied > 0) apps.add(row.AppCode)
    if (row.Calls > 0 && (!lastCallDay || day > lastCallDay)) lastCallDay = day
    if (row.Denied > 0 && (!lastDeniedDay || day > lastDeniedDay)) {
      lastDeniedDay = day
    }
    if (day > from28) {
      calls28d += row.Calls
      errors28d += row.Errors
      denied28d += row.Denied
      if (row.Calls > 0) activeDays.add(day)
      if (day > from7) calls7d += row.Calls
    } else if (day > from56) {
      callsPrev28d += row.Calls
    }
  }

  return {
    apps: [...apps].toSorted(),
    calls28d,
    callsPrev28d,
    calls7d,
    trendPct: mcpTrendPct(calls28d, callsPrev28d),
    errors28d,
    errorPct28d:
      calls28d > 0 ? Math.round((errors28d * 1000) / calls28d) / 10 : null,
    denied28d,
    activeDays28d: activeDays.size,
    lastCallDay,
    lastDeniedDay,
  }
}

/**
 * Zero-filled daily tool-call totals over the trailing `historyDays`,
 * oldest first, in the shape the history strip renders (value = rawCount =
 * calls: MCP calls are not action-weighted). Quiet days must show as quiet.
 */
export function deriveMcpHistory(
  days: UsageMcpDayRow[],
  historyDays: number,
  anchor: Date = new Date(),
): UsageDailyTotal[] {
  const callsByDay = new Map<string, number>()
  for (const row of days) {
    const day = String(row.Date).slice(0, 10)
    callsByDay.set(day, (callsByDay.get(day) ?? 0) + row.Calls)
  }
  return Array.from({ length: historyDays }, (_, index) => {
    const day = daysAgo(anchor, historyDays - 1 - index)
    const calls = callsByDay.get(day) ?? 0
    return { day, value: calls, rawCount: calls }
  })
}

/**
 * Collapses a breakdown list across MCP servers: a tool or client called on
 * both servers becomes one entry (the shares and the "most used" ranking
 * are about the key, not the server), re-sorted most 28-day calls first.
 * Calls add up; Users28d does not — the procs already count the distinct
 * people behind a key across servers and repeat that value on each server
 * row, so a person on both servers is one person here.
 */
export function mergeMcpCounts(rows: UsageMcpCountRow[]) {
  const byKey = new Map<string, UsageMcpCountRow>()
  for (const row of rows) {
    const existing = byKey.get(row.Key)
    if (!existing) {
      byKey.set(row.Key, { ...row })
      continue
    }
    existing.Calls28d += row.Calls28d
    existing.CallsPrev28d += row.CallsPrev28d
    existing.Errors28d += row.Errors28d
    existing.Users28d = Math.max(existing.Users28d, row.Users28d)
  }
  return [...byKey.values()].sort(
    (a, b) =>
      b.Calls28d - a.Calls28d ||
      b.CallsPrev28d - a.CallsPrev28d ||
      a.Key.localeCompare(b.Key),
  )
}

/** True when the series carries any MCP activity worth a section at all. */
export function hasMcpActivity(days: UsageMcpDayRow[]) {
  return days.some((row) => row.Calls > 0 || row.Denied > 0)
}

/** ISO week number of a date (weeks start Monday; week 1 holds 4 January). */
export function isoWeekNumber(date: Date) {
  const utc = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  )
  const day = utc.getUTCDay() || 7
  utc.setUTCDate(utc.getUTCDate() + 4 - day)
  const yearStart = Date.UTC(utc.getUTCFullYear(), 0, 1)
  return Math.ceil(((utc.getTime() - yearStart) / ONE_DAY_IN_MS + 1) / 7)
}

/** "Week 43" for a week's Monday. */
export function formatIsoWeek(weekStart: string | Date) {
  return `Week ${isoWeekNumber(new Date(weekStart))}`
}

/** Three-letter months, UTC-indexed; the chart and the table share them. */
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

/** "19–25 Oct" / "28 Sep – 4 Oct" for a week's Monday. */
export function formatIsoWeekRange(weekStart: string | Date) {
  const start = new Date(weekStart)
  const end = new Date(start.getTime() + 6 * ONE_DAY_IN_MS)
  const sameMonth = start.getUTCMonth() === end.getUTCMonth()
  const day = (date: Date) => date.getUTCDate()
  const month = (date: Date) => MONTHS_SHORT[date.getUTCMonth()]
  return sameMonth
    ? `${day(start)}–${day(end)} ${month(end)}`
    : `${day(start)} ${month(start)} – ${day(end)} ${month(end)}`
}

export type UsageMcpToolGroupShare = {
  group: McpToolGroup
  label: string
  calls: number
  /** Share of all tool calls in the window, 0–1. */
  share: number
  /** Distinct tools of the group that were called. */
  tools: number
}

/**
 * The 28-day tool calls grouped into the report's use-case areas, biggest
 * first: the six "what they use it for" tiles. Every tool lands somewhere
 * (unknown codes under "Help, templates & other"), so the shares add up.
 */
export function deriveMcpToolGroups(
  tools: UsageMcpCountRow[],
): UsageMcpToolGroupShare[] {
  const total = tools.reduce((sum, tool) => sum + tool.Calls28d, 0)
  const byGroup = new Map<McpToolGroup, { calls: number; tools: number }>()
  for (const tool of tools) {
    if (tool.Calls28d <= 0) continue
    const group = mcpToolGroup(tool.Key)
    const entry = byGroup.get(group) ?? { calls: 0, tools: 0 }
    entry.calls += tool.Calls28d
    entry.tools += 1
    byGroup.set(group, entry)
  }
  return [...byGroup.entries()]
    .map(([group, entry]) => ({
      group,
      label: MCP_TOOL_GROUP_LABELS[group],
      calls: entry.calls,
      share: total > 0 ? entry.calls / total : 0,
      tools: entry.tools,
    }))
    .toSorted((a, b) => b.calls - a.calls)
}
